import { useEffect, useMemo, useState } from 'react';
import {
  Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle,
  IconButton, ListSubheader, MenuItem, Stack, TextField, Tooltip, Typography, CircularProgress,
} from '@mui/material';
import {
  Add, Edit as EditIcon, Done, DriveFileRenameOutline, Delete, FileUpload, FileDownload,
  ContentCopy, People, PeopleOutline, Refresh,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import type { Layout } from 'react-grid-layout';
import { DATE_PRESETS } from '../../utils/date-presets';
import { useDashboards } from './useDashboards';
import DashboardCanvas from './DashboardCanvas';
import PanelEditorDialog from './PanelEditorDialog';
import { buildExampleDashboard } from './examples';
import { exportDashboard, parseImportedDashboard } from './dashboard-io';
import {
  uid, emptyConfig, type DashboardConfig, type DashboardPanel,
} from './types';

const ACTIVE_KEY = 'mx.dashboards.activeId';

function newPanel(y: number): DashboardPanel {
  return {
    id: uid('p'), title: 'New panel', viz: 'line',
    query: { group_by: 'date', metrics: ['clicks', 'conversions'] },
    layout: { x: 0, y, w: 6, h: 6 },
  };
}

export default function DashboardsPage() {
  const { boards, loading, saving, saveError, createBoard, updateBoard, deleteBoard } = useDashboards();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const refreshPanels = () => queryClient.invalidateQueries({ queryKey: ['dashboard-panel'] });
  // The empty-state preview — built once, rendered read-only until the viewer saves it.
  const previewConfig = useMemo(() => buildExampleDashboard().config, []);

  const [activeId, setActiveId] = useState<string>(() => localStorage.getItem(ACTIVE_KEY) || '');
  const active = useMemo(() => boards.find((d) => d.id === activeId) ?? boards[0], [boards, activeId]);
  useEffect(() => { if (active) localStorage.setItem(ACTIVE_KEY, active.id); }, [active]);

  const isOwner = active?.is_owner ?? false;
  const [editMode, setEditMode] = useState(false);
  const [editorPanel, setEditorPanel] = useState<DashboardPanel | null>(null);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameName, setRenameName] = useState('');
  const [renameDesc, setRenameDesc] = useState('');
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState('');

  useEffect(() => { if (saveError) enqueueSnackbar('Could not save the dashboard', { variant: 'error' }); }, [saveError, enqueueSnackbar]);
  useEffect(() => { if (!isOwner) setEditMode(false); }, [isOwner, activeId]);

  const updateConfig = (mut: (c: DashboardConfig) => DashboardConfig) => {
    if (!active) return;
    updateBoard(active.id, { config: mut(active.config) });
  };

  const addPanel = () => {
    if (!active) return;
    const maxY = active.config.panels.reduce((m, p) => Math.max(m, p.layout.y + p.layout.h), 0);
    const panel = newPanel(maxY);
    updateConfig((c) => ({ ...c, panels: [...c.panels, panel] }));
    setEditorPanel(panel);
  };
  const savePanel = (updated: DashboardPanel) => {
    updateConfig((c) => ({ ...c, panels: c.panels.map((p) => (p.id === updated.id ? updated : p)) }));
    setEditorPanel(null);
  };
  const duplicatePanel = (panel: DashboardPanel) => {
    const copy: DashboardPanel = {
      ...panel, id: uid('p'), title: `${panel.title} (copy)`,
      layout: { ...panel.layout, y: panel.layout.y + panel.layout.h },
    };
    updateConfig((c) => ({ ...c, panels: [...c.panels, copy] }));
  };
  const deletePanel = (panel: DashboardPanel) =>
    updateConfig((c) => ({ ...c, panels: c.panels.filter((p) => p.id !== panel.id) }));
  const handleLayout = (layout: Layout[]) => updateConfig((c) => ({
    ...c,
    panels: c.panels.map((p) => {
      const l = layout.find((x) => x.i === p.id);
      return l ? { ...p, layout: { x: l.x, y: l.y, w: l.w, h: l.h } } : p;
    }),
  }));

  const addAndSelect = async (input: Parameters<typeof createBoard>[0]) => {
    try { const b = await createBoard(input); setActiveId(b.id); return b; } catch { enqueueSnackbar('Could not create dashboard', { variant: 'error' }); }
  };
  const handleNew = async () => { await addAndSelect({ name: 'New dashboard', config: emptyConfig() }); setEditMode(true); };
  const handleExample = () => addAndSelect(buildExampleDashboard());
  const handleClone = () => {
    if (!active) return;
    addAndSelect({ name: `${active.name} (copy)`, description: active.description, config: active.config })
      .then((b) => { if (b) enqueueSnackbar('Copied to your dashboards', { variant: 'success' }); });
  };
  const handleDelete = async () => {
    if (!active) return;
    const rest = boards.filter((d) => d.id !== active.id);
    try { await deleteBoard(active.id); setActiveId(rest[0]?.id ?? ''); } catch { enqueueSnackbar('Could not delete', { variant: 'error' }); }
    setDeleteOpen(false);
  };
  const handleExport = async () => {
    if (!active) return;
    try { await navigator.clipboard.writeText(exportDashboard(active)); enqueueSnackbar('Dashboard JSON copied', { variant: 'success' }); }
    catch { enqueueSnackbar('Could not access the clipboard', { variant: 'error' }); }
  };
  const handleImport = async () => {
    const res = parseImportedDashboard(importText);
    if (!res.ok) { setImportError(res.error); return; }
    await addAndSelect({ name: res.name, description: res.description, config: res.config });
    setImportOpen(false); setImportText(''); setImportError('');
    enqueueSnackbar(`Imported "${res.name}"`, { variant: 'success' });
  };

  if (loading) {
    return <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>;
  }

  if (!active) {
    // No boards yet: show the Overview filled with the viewer's live data as a read-only
    // preview, and let them keep it in one click (saved as their own) — no write until then.
    return (
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" fontWeight={600}>Overview</Typography>
            <Typography variant="body2" color="text.secondary">A starter dashboard from your tracking data — save it to make it yours and edit freely.</Typography>
          </Box>
          <Box sx={{ flexGrow: 1 }} />
          <Button variant="contained" onClick={handleExample}>Save as my dashboard</Button>
          <Button onClick={handleNew}>New blank</Button>
          <Button startIcon={<FileUpload />} onClick={() => setImportOpen(true)}>Import JSON</Button>
        </Stack>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
          <DashboardCanvas
            dashboard={previewConfig}
            editMode={false}
            onLayoutChange={() => {}}
            onEditPanel={() => {}}
            onDuplicatePanel={() => {}}
            onDeletePanel={() => {}}
          />
        </Box>
        <ImportDialog open={importOpen} text={importText} error={importError} onText={(t) => { setImportText(t); setImportError(''); }} onClose={() => setImportOpen(false)} onImport={handleImport} />
      </Box>
    );
  }

  const mine = boards.filter((b) => b.is_owner);
  const shared = boards.filter((b) => !b.is_owner);

  return (
    <Box sx={{ p: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 0.5 }}>
        <TextField select size="small" value={active.id} onChange={(e) => setActiveId(e.target.value)} sx={{ minWidth: 220 }}>
          {mine.length > 0 && <ListSubheader>My dashboards</ListSubheader>}
          {mine.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
          {shared.length > 0 && <ListSubheader>Shared with me</ListSubheader>}
          {shared.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
        </TextField>

        {isOwner ? (
          <>
            <Tooltip title="New dashboard"><IconButton onClick={handleNew}><Add /></IconButton></Tooltip>
            <Tooltip title="Rename / describe"><IconButton onClick={() => { setRenameName(active.name); setRenameDesc(active.description ?? ''); setRenameOpen(true); }}><DriveFileRenameOutline /></IconButton></Tooltip>
            <Tooltip title="Delete dashboard"><IconButton onClick={() => setDeleteOpen(true)}><Delete /></IconButton></Tooltip>
            <Tooltip title={active.shared ? 'Shared with your workspace — click to make private' : 'Share with your workspace'}>
              <Button size="small" variant={active.shared ? 'contained' : 'outlined'} color={active.shared ? 'primary' : 'inherit'}
                startIcon={active.shared ? <People /> : <PeopleOutline />}
                onClick={() => updateBoard(active.id, { shared: !active.shared }, { immediate: true })}
              >
                {active.shared ? 'Shared' : 'Share'}
              </Button>
            </Tooltip>
          </>
        ) : (
          <>
            <Chip size="small" icon={<People sx={{ fontSize: 16 }} />} label={`Shared by ${active.owner_name || active.owner_email || 'another user'}`} />
            <Button size="small" startIcon={<ContentCopy />} onClick={handleClone}>Clone to edit</Button>
          </>
        )}

        <Box sx={{ flexGrow: 1 }} />
        {saving && <CircularProgress size={16} sx={{ mr: 1 }} />}

        <TextField select size="small" label="Range" value={active.config.time_preset}
          onChange={(e) => updateConfig((c) => ({ ...c, time_preset: e.target.value as DashboardConfig['time_preset'] }))}
          disabled={!isOwner} sx={{ minWidth: 150 }}
        >
          {DATE_PRESETS.map((p) => <MenuItem key={p.key} value={p.key}>{p.title}</MenuItem>)}
        </TextField>
        <Tooltip title="Refresh data"><IconButton onClick={refreshPanels}><Refresh /></IconButton></Tooltip>
        <Tooltip title="Copy dashboard JSON"><IconButton onClick={handleExport}><FileDownload /></IconButton></Tooltip>
        {isOwner && <Tooltip title="Import JSON"><IconButton onClick={() => setImportOpen(true)}><FileUpload /></IconButton></Tooltip>}
        {isOwner && editMode && <Button startIcon={<Add />} onClick={addPanel} size="small" variant="outlined">Panel</Button>}
        {isOwner && (
          <Button startIcon={editMode ? <Done /> : <EditIcon />} onClick={() => setEditMode((v) => !v)} size="small" variant={editMode ? 'contained' : 'outlined'}>
            {editMode ? 'Done' : 'Edit'}
          </Button>
        )}
      </Stack>
      {active.description && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{active.description}</Typography>}

      <Box sx={{ flexGrow: 1, minHeight: 0, overflow: 'auto' }}>
        {active.config.panels.length === 0 ? (
          <Box sx={{ textAlign: 'center', mt: 8, color: 'text.secondary' }}>
            <Typography sx={{ mb: 2 }}>This dashboard is empty.</Typography>
            {isOwner && <Button variant="contained" startIcon={<Add />} onClick={() => { setEditMode(true); addPanel(); }}>Add a panel</Button>}
          </Box>
        ) : (
          <DashboardCanvas
            dashboard={active.config}
            editMode={isOwner && editMode}
            onLayoutChange={handleLayout}
            onEditPanel={setEditorPanel}
            onDuplicatePanel={duplicatePanel}
            onDeletePanel={deletePanel}
          />
        )}
      </Box>

      <PanelEditorDialog panel={editorPanel} open={!!editorPanel} onClose={() => setEditorPanel(null)} onSave={savePanel} />

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Dashboard details</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField autoFocus fullWidth size="small" label="Name" value={renameName} onChange={(e) => setRenameName(e.target.value)} />
            <TextField fullWidth size="small" label="Subtitle" placeholder="optional" value={renameDesc} onChange={(e) => setRenameDesc(e.target.value)} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!renameName.trim()}
            onClick={() => { updateBoard(active.id, { name: renameName.trim(), description: renameDesc.trim() || null }, { immediate: true }); setRenameOpen(false); }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete dashboard?</DialogTitle>
        <DialogContent><DialogContentText>&ldquo;{active.name}&rdquo; will be permanently removed{active.shared ? ', including for everyone it is shared with' : ''}.</DialogContentText></DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Delete</Button>
        </DialogActions>
      </Dialog>

      <ImportDialog open={importOpen} text={importText} error={importError} onText={(t) => { setImportText(t); setImportError(''); }} onClose={() => setImportOpen(false)} onImport={handleImport} />
    </Box>
  );
}

function ImportDialog({
  open, text, error, onText, onClose, onImport,
}: {
  open: boolean; text: string; error: string;
  onText: (t: string) => void; onClose: () => void; onImport: () => void;
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import dashboard</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 1 }}>Paste a dashboard JSON (from another user&rsquo;s Copy). It is validated and added as a new dashboard you own.</DialogContentText>
        <TextField multiline minRows={8} fullWidth value={text} onChange={(e) => onText(e.target.value)}
          error={!!error} helperText={error || ' '} placeholder='{ "version": 1, "name": "...", "panels": [ ... ] }'
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={!text.trim()} onClick={onImport}>Import</Button>
      </DialogActions>
    </Dialog>
  );
}
